import {
	checkStoredIdleExpiry,
	IDLE_ACTIVITY_STORAGE_KEYS,
	parseIdleActivityRecord,
	serializeIdleActivityRecord,
} from '@supply-mover-2-0/core';
import {
	restorePersistedSession,
	usePortalAuthStore,
} from '../stores/portalAuthStore';

/**
 * PD-17338 — the storefront checks the idle limit BEFORE it uses a stored
 * session, not after.
 *
 * The idle sign-out (PortalIdleTimeout) only starts once /auth/me has told
 * it the tenant's limit. So a shopper reopening the storefront app hours
 * later had their old session restored with an /auth/me call that the server
 * answered and counted as storefront activity, moving their Last Active in
 * the CRM, and only then were they signed out. The same could happen when
 * the app came back from the background: whichever request fired first on
 * resume went out before the idle clock had a chance to check.
 *
 * The idle record carries its limit (see `checkStoredIdleExpiry`), so the
 * check needs no network. It runs before the start-up session restore and
 * before every request that would carry a session (apiClient). An expired
 * session is cleared locally at once, so nothing after it can use the token,
 * and PortalIdleTimeout shows the shopper the usual idle sign-out.
 */

/** A session this page ended because it had been idle past the limit. */
export interface IdleExpiredSessionModel {
	/** The limit that ran out, for the sign-out message; null when unknown. */
	limitMinutes: number | null;
	/** The session's tokens, already cleared from the store, for the server revoke. */
	token: string | null;
	refreshToken: string | null;
}

type IdleExpiredListener = (session: IdleExpiredSessionModel) => void;

let listener: IdleExpiredListener | null = null;
/** An expiry found before PortalIdleTimeout subscribed (the start-up check). */
let undelivered: IdleExpiredSessionModel | null = null;

/**
 * Access tokens ended for idleness in this page. A request built before the
 * sign-out still holds the old token (a React Query retry, a poll that was
 * already scheduled when the app resumed); the API client refuses to send
 * these instead of letting the old session be used one more time.
 */
const idleExpiredAccessTokens = new Set<string>();

function readIdleRecord(): string | null {
	try {
		return localStorage.getItem(IDLE_ACTIVITY_STORAGE_KEYS.storefront);
	} catch {
		return null;
	}
}

/**
 * Mark the shared record as signed out before clearing the session, the way
 * the idle clock does for its own sign-out, so other open tabs follow with
 * the same message instead of being signed out silently.
 */
function markOtherTabs(raw: string | null): void {
	const record = parseIdleActivityRecord(raw);
	if (record == null) {
		return;
	}
	try {
		localStorage.setItem(
			IDLE_ACTIVITY_STORAGE_KEYS.storefront,
			serializeIdleActivityRecord({ ...record, timedOut: true }),
		);
	} catch {
		// Storage refused the write: other tabs still sign out through the
		// cleared session record, just without the message.
	}
}

/** Remember an access token ended for idleness so it is never sent again. */
export function markAccessTokenIdleExpired(token: string | null): void {
	if (token != null && token.length > 0) {
		idleExpiredAccessTokens.add(token);
	}
}

export function isIdleExpiredAccessToken(token: string): boolean {
	return idleExpiredAccessTokens.has(token);
}

/**
 * End the signed-in session now if it has already been idle past its limit.
 * Synchronous: when it returns true the store and the stored session are
 * cleared, so the caller must not send its request. Returns false when the
 * session is still inside the limit, the record cannot tell, or nobody is
 * signed in.
 */
export function endSessionIfIdleExpired(now: number = Date.now()): boolean {
	const raw = readIdleRecord();
	const check = checkStoredIdleExpiry(raw, now);
	if (!check.expired) {
		return false;
	}

	const state = usePortalAuthStore.getState();
	// At start-up the session exists only on disk; later it is in the store.
	const stored =
		state.token == null && state.refreshToken == null
			? restorePersistedSession()
			: null;
	const token = state.token ?? stored?.token ?? null;
	const refreshToken = state.refreshToken ?? stored?.refreshToken ?? null;
	if (token == null && refreshToken == null) {
		return false;
	}

	markOtherTabs(raw);
	markAccessTokenIdleExpired(token);
	// The storefront's sign-out reset: clears the tokens in memory and on
	// disk, removes the idle record and keeps the language choice.
	state.reset();

	const session: IdleExpiredSessionModel = {
		limitMinutes: check.limitMinutes,
		token,
		refreshToken,
	};
	if (listener != null) {
		listener(session);
	} else {
		undelivered = session;
	}
	return true;
}

/**
 * Hear about sessions ended by `endSessionIfIdleExpired`. One listener
 * (PortalIdleTimeout); an expiry found before it subscribed is handed over
 * on subscribe.
 */
export function onIdleSessionExpired(next: IdleExpiredListener): () => void {
	listener = next;
	if (undelivered != null) {
		const session = undelivered;
		undelivered = null;
		next(session);
	}
	return (): void => {
		if (listener === next) {
			listener = null;
		}
	};
}
